import React from 'react';
import { format } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { Users, TrendingUp, Target, Heart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TabsContent } from '../ui/tabs';
import { formatNumber } from './constants';

const COUNTRY_FLAGS = {
  'United States': '🇺🇸',
  'United Kingdom': '🇬🇧',
  Canada: '🇨🇦',
  Australia: '🇦🇺',
  Germany: '🇩🇪',
};

const AudienceTab = ({ audience, analytics }) => {
  if (!audience) return <TabsContent value="audience" />;

  return (
    <TabsContent value="audience" className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Users, color: 'bg-indigo-500', value: formatNumber(audience.total_followers), label: 'Total Followers' },
          { icon: TrendingUp, color: 'bg-emerald-500', value: `+${formatNumber(audience.follower_growth_30d)}`, label: '30-Day Growth' },
          { icon: Target, color: 'bg-amber-500', value: `${analytics?.summary?.engagement_rate || 0}%`, label: 'Engagement Rate' },
          { icon: Heart, color: 'bg-pink-500', value: formatNumber(analytics?.summary?.avg_engagement_per_post || 0), label: 'Avg. Engagement/Post' },
        ].map(({ icon: Icon, color, value, label }) => (
          <Card key={label} className="border-slate-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{value}</p>
                  <p className="text-sm text-slate-500">{label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Age Demographics */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Age Demographics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={audience.age_demographics}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="range" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip
                    formatter={(value) => `${value}%`}
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  />
                  <Bar dataKey="percentage" name="Percentage" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Locations */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Top Locations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {audience.top_locations.map((loc) => (
                <div key={loc.country} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-lg">
                    {COUNTRY_FLAGS[loc.country] || '🌍'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">{loc.country}</span>
                      <span className="text-sm text-slate-500">{loc.percentage}%</span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full"
                        style={{ width: `${loc.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Follower Growth Chart */}
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Follower Growth</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={audience.follower_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  fontSize={11}
                  tickFormatter={(value) => format(new Date(value), 'MMM d')}
                />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  labelFormatter={(value) => format(new Date(value), 'MMM d, yyyy')}
                />
                <Line type="monotone" dataKey="followers" stroke="#10B981" strokeWidth={2} dot={false} name="Followers" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  );
};

export default AudienceTab;
